import { useEffect, useState } from "react";
import api, { fmtUSD, apiError } from "@/lib/api";
import { Panel, Metric, Money } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const LABELS = {
  capital_contributions: "Capital Contributions", market_appreciation: "Market Appreciation",
  realized_trading_gains: "Realized Trading Gains", dividends: "Dividends", interest: "Interest",
  staking_rewards: "Staking Rewards", card_rewards: "Card & Other Rewards", referral_revenue: "Referral Revenue",
  securities_lending: "Securities Lending", other_income: "Other Income",
  fees_and_trading_costs: "Fees & Trading Costs", tax_liability: "Tax Liability",
};

export default function Performance() {
  const [d, setD] = useState(null);
  const [bench, setBench] = useState(null);
  const [alpha, setAlpha] = useState(null);
  const [defineOpen, setDefineOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const defineBenchmark = async () => {
    setBusy(true);
    try {
      await api.post("/benchmark/define", { confirm: true, new_period: Boolean(bench?.defined) });
      toast.success(bench?.defined ? "New benchmark period started — prior period archived immutably"
                                   : "Benchmark defined — immutable baseline set");
      setDefineOpen(false);
      const r = await api.get("/benchmark"); setBench(r.data);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  useEffect(() => {
    api.get("/performance").then((r) => setD(r.data)).catch(() => {});
    api.get("/benchmark").then((r) => setBench(r.data)).catch(() => {});
    api.get("/execution-alpha").then((r) => setAlpha(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  const chart = (d.snapshots || []).map((s) => ({ date: s.date.slice(5), value: s.gross_value_latest ?? s.gross_value }));
  return (
    <div className="space-y-5" data-testid="performance-page">
      <h2 className="font-display text-xl font-bold">Performance Attribution</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Metric label="Total Money Contributed" value={fmtUSD(d.total_money_contributed)} testId="perf-contributed" />
        <Metric label="Total Value Created by Treasury" value={fmtUSD(d.total_value_created)} tone={d.total_value_created >= 0 ? "pos" : "neg"} testId="perf-value-created" />
        <Metric label="Gross Treasury Value" value={fmtUSD(d.gross_treasury_value)} testId="perf-gross" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Benchmark — Passive Policy Buy & Hold" testId="benchmark-panel">
          {!bench || !bench.defined ? (
            <div className="space-y-3" data-testid="benchmark-empty">
              <div className="text-slate-500 text-sm">
                No benchmark defined for this account. The benchmark is a passive buy-and-hold basket of the Core
                Digital policy weights, snapshotted at definition and immutable for its comparison period.
              </div>
              <Button data-testid="benchmark-define-button" onClick={() => setDefineOpen(true)} size="sm"
                className="bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">DEFINE BENCHMARK</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm font-mono">
              <div><div className="metric-label">Benchmark return</div><div className="text-slate-100 mt-0.5" data-testid="benchmark-return">{bench.benchmark_return_pct}%</div></div>
              <div><div className="metric-label">Account return</div><div className="text-slate-100 mt-0.5" data-testid="benchmark-account-return">{bench.account_return_pct}%</div></div>
              <div><div className="metric-label">Gross alpha vs benchmark</div><div className={`mt-0.5 ${bench.gross_alpha_pct >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="benchmark-alpha">{bench.gross_alpha_pct > 0 ? "+" : ""}{bench.gross_alpha_pct}%</div></div>
              <div><div className="metric-label">Benchmark value</div><div className="text-slate-300 mt-0.5" data-testid="benchmark-value">{fmtUSD(bench.benchmark_value)}</div></div>
              <div><div className="metric-label">Period</div><div className="text-slate-300 mt-0.5" data-testid="benchmark-period">#{bench.period} · since {new Date(bench.defined_at).toLocaleDateString()}</div></div>
              <div><div className="metric-label">Inflows after start</div><div className="text-slate-500 mt-0.5" data-testid="benchmark-inflows-after">{fmtUSD(bench.inflows_after_start)}</div></div>
            </div>
          )}
          {bench?.defined && (
            <button data-testid="benchmark-new-period-button" onClick={() => setDefineOpen(true)}
              className="text-[10px] font-mono text-slate-600 hover:text-slate-400 mt-3 underline underline-offset-2">
              START NEW COMPARISON PERIOD (ARCHIVES CURRENT IMMUTABLY)
            </button>
          )}
        </Panel>
        <Panel title="Execution Alpha — Model vs Fill" testId="execution-alpha-panel">
          {!alpha || alpha.total.count === 0 ? (
            <div className="text-slate-500 text-sm" data-testid="execution-alpha-empty">
              No executed trades yet. Execution alpha measures decision price vs actual fill, fees and slippage —
              split paper vs live.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm font-mono">
                <div><div className="metric-label">Execution alpha</div><div className={`mt-0.5 ${alpha.total.execution_alpha_usd >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="execution-alpha-total">{fmtUSD(alpha.total.execution_alpha_usd)}</div></div>
                <div><div className="metric-label">Fees paid</div><div className="text-slate-100 mt-0.5" data-testid="execution-alpha-fees">{fmtUSD(alpha.total.fees_usd)}</div></div>
                <div><div className="metric-label">Avg |slippage|</div><div className="text-slate-100 mt-0.5" data-testid="execution-alpha-slippage">{alpha.total.avg_abs_slippage_bps} bps</div></div>
                <div><div className="metric-label">Trades</div><div className="text-slate-300 mt-0.5" data-testid="execution-alpha-count">{alpha.total.count}</div></div>
              </div>
              <div className="text-[10px] font-mono text-slate-500" data-testid="execution-alpha-split">
                PAPER: {alpha.paper.count} trades · {fmtUSD(alpha.paper.execution_alpha_usd)} alpha · {fmtUSD(alpha.paper.fees_usd)} fees
                · LIVE: {alpha.live.count} trades · {fmtUSD(alpha.live.execution_alpha_usd)} alpha · {fmtUSD(alpha.live.fees_usd)} fees
              </div>
            </div>
          )}
        </Panel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Where Value Is Coming From" testId="perf-attribution">
          <div className="space-y-1.5">
            {Object.entries(d.attribution).map(([k, v]) => (
              <div key={k} className="flex justify-between items-center border-b border-[#222630] pb-1.5 text-sm" data-testid={`attr-${k}`}>
                <span className="text-slate-400">{LABELS[k] || k}</span>
                <Money value={v} tone={v > 0 ? "pos" : v < 0 ? "neg" : undefined} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Treasury Value (daily close)" testId="perf-chart">
          {chart.length === 0 ? <div className="text-slate-500 text-sm py-8 text-center">Snapshots accumulate daily.</div> : (
            <div className="h-56 w-full" style={{ minHeight: 224 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart}>
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} stroke="#2D323E" />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10 }} stroke="#2D323E" domain={["auto", "auto"]}
                    tickFormatter={(v) => `$${v.toLocaleString()}`} width={70} />
                  <Tooltip contentStyle={{ background: "#17191E", border: "1px solid #2D323E", fontSize: 12 }}
                    formatter={(v) => [fmtUSD(v), "Gross Treasury"]} />
                  <Line type="stepAfter" dataKey="value" stroke="#D4AF37" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>
      <AlertDialog open={defineOpen} onOpenChange={setDefineOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bench?.defined ? "Start a new benchmark period?" : "Define the benchmark?"}</AlertDialogTitle>
            <AlertDialogDescription>
              This snapshots the current Core Digital policy weights, live start prices and contributed capital
              as the comparison baseline. The benchmark is IMMUTABLE for its period — starting a new period
              archives the current one permanently in the audit record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="benchmark-define-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="benchmark-define-confirm" onClick={defineBenchmark} disabled={busy}>
              {busy ? "DEFINING…" : "CONFIRM — SET BASELINE"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
